import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Promotions } from '../interfaces/promotions/promotions';
import { Observable, takeUntil, catchError, throwError, Subject, BehaviorSubject } from 'rxjs';
import { ConfigService } from './core/config.service';
import { VuePromotionDTO } from '../interfaces/promotions/VuePromotion';
import { FilterPromotionsRequest } from '../interfaces/promotions/filterPromotionsRequest';
import { EanOrphelin } from '../interfaces/promotions/EanOrphelin';

@Injectable({
  providedIn: 'root',
})
/**
 * Service to handle promotions related operations.
 *
 * @remarks
 * This service provides methods to update the ID of an adherent and fetch promotions based on the adherent's ID.
 *
 * @example
 * ```typescript
 * constructor(private promotionsService: PromotionsService) {}
 *
 * this.promotionsService.updateId(123);
 * this.promotionsService.getPromotionsByAdherentId(123).subscribe(promotions => {
 *   console.log(promotions);
 * });
 * ```
 */
export class PromotionsService {
  private readonly id = new BehaviorSubject<number>(0);
  private readonly _destroying$ = new Subject<void>();
  public idAdherent = this.id.asObservable();

  constructor(private readonly configService: ConfigService, private readonly http: HttpClient) {}

  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Updates the current ID with the provided value.
   *
   * @param value - The new value to set for the ID.
   */
  updateId(value: any) {
    this.id.next(value);
  }

  getId(): number {
    return this.id.value;
  }

  /**
   * Retrieves a list of promotions for a specific adherent by their ID.
   *
   * @param idAdherent - The ID of the adherent whose promotions are to be retrieved.
   * @returns An Observable that emits an array of Promotions objects.
   *
   * @throws Will throw an error if the HTTP request fails.
   */
  public getPromotionsByAdherentId(idAdherent: number): Observable<Promotions[]> {
    return this.http
      .get<Promotions[]>(
        `${this.configService.config.backendUri}/promotions?idAdherent=${idAdherent}`,
      )
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  /**
   * Retrieves a filtered list of promotions for a specific adherent.
   *
   * @param idAdherent - The ID of the adherent for whom promotions are being retrieved.
   * @param filter - The filter criteria to apply to the promotions request.
   * @returns An Observable that emits an array of Promotions objects.
   */
  public getPromotionsFiltered(
    idAdherent: number,
    filter: FilterPromotionsRequest,
  ): Observable<Promotions[]> {
    return this.http
      .post<Promotions[]>(
        `${this.configService.config.backendUri}/promotions/filter/${idAdherent}`,
        filter,
      )
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  /**
   * Updates the selection status of a promotion.
   *
   * @param id - The unique identifier of the promotion.
   * @param selected - The selection status to be set (true for selected, false for not selected).
   * @returns An Observable that emits the server's response.
   */
  public putSelectionPromotion(id: number, selected: boolean): Observable<any> {
    return this.http
      .put(
        `${this.configService.config.backendUri}/promotions/${id}/select?selected=${selected}`,
        null,
      )
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  /**
   * Updates the selection status of a list of promotions.
   *
   * @param ids - An array of promotion IDs to update.
   * @param selected - A boolean indicating whether the promotions should be marked as selected or not.
   * @returns An Observable that emits the server response.
   */
  public putSelectionsPromotionsList(ids: number[], selected: boolean): Observable<any> {
    return this.http
      .put(`${this.configService.config.backendUri}/promotions/select?selected=${selected}`, ids)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }
  public getPromotionsDetailsByPromotionIdAndAdherentId(
    idPromotion: number,
    idAdherent: number,
  ): Observable<VuePromotionDTO> {
    return this.http
      .get<VuePromotionDTO>(
        `${this.configService.config.backendUri}/promotionsdetail/${idPromotion}/${idAdherent}`,
      )
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }
  public updatePromotionStatut(
    idPromotion: number,
    idAdherent: number,
    selectedStatut: string,
  ): Observable<any> {
    return this.http
      .put(
        `${this.configService.config.backendUri}/promotions/update-statut/${idPromotion}/${idAdherent}?statut=${selectedStatut}`,
        null,
      )
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  getEanOrphelinByPromotionIdAndAdherent(
    idAdherent: number,
    idPromotion: number,
  ): Observable<EanOrphelin[]> {
    return this.http
      .get<EanOrphelin[]>(
        `${this.configService.config.backendUri}/orphan-ean/${idAdherent}/${idPromotion}`,
      )
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  public linkEanOrphelinToPromotionProduct(
    idEanOrphelin: number,
    idEan: number,
  ): Observable<VuePromotionDTO> {
    return this.http
      .post<VuePromotionDTO>(
        `${this.configService.config.backendUri}/orphan-ean/attach-ean/${idEanOrphelin}/${idEan}`,
        null,
      )
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  public linkOffEanOrphelinFromPromotionProduct(idEan: number): Observable<VuePromotionDTO> {
    return this.http
      .post<VuePromotionDTO>(
        `${this.configService.config.backendUri}/orphan-ean/dettach-ean/${idEan}`,
        null,
      )
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }
}
